import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Building2, ArrowLeft, TrendingUp, DollarSign, PieChart } from "lucide-react";
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useNavigate } from "react-router-dom";

// Memoized tooltip style to prevent re-renders
const TOOLTIP_STYLE = { 
  backgroundColor: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '0.125rem'
};

export const ResultsView = ({ project, onBack }) => {
  const navigate = useNavigate();
  const results = project.results;
  const fsiBreakdown = project.fsi_breakdown;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const costBreakdownData = [
    { name: 'Construction', value: results.cost_breakdown.construction },
    { name: 'Land', value: results.cost_breakdown.land },
    { name: 'Legal', value: results.cost_breakdown.legal },
    { name: 'Marketing', value: results.cost_breakdown.marketing },
    { name: 'Other', value: results.cost_breakdown.other_costs },
    { name: 'Contingency', value: results.cost_breakdown.contingency },
  ];

  const revenueBreakdownData = [
    { name: 'Flats', value: results.revenue_breakdown.flats },
    { name: 'Offices', value: results.revenue_breakdown.offices },
    { name: 'Shops', value: results.revenue_breakdown.shops },
  ];

  const revenueVsCostData = [
    {
      name: 'Financial Overview',
      'Total Costs': results.total_costs,
      'Total Revenue': results.total_revenue,
      'Profit': results.profit
    }
  ];

  const COLORS = ['hsl(var(--chart-2))', 'hsl(var(--chart-4))', 'hsl(var(--chart-3))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--chart-5))'];
  const REVENUE_COLORS = ['hsl(var(--chart-1))', 'hsl(var(--accent))', 'hsl(var(--chart-3))'];

  const isViable = results.roi > 15 && results.profit > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              data-testid="back-calculator-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              New Analysis
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <span className="font-heading font-bold text-lg tracking-tight">Analysis Results</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/projects")}
              data-testid="view-all-projects-btn"
            >
              View All Projects
            </Button>
            <Button
              onClick={() => navigate("/")}
              data-testid="home-btn"
            >
              Home
            </Button>
          </div>
        </nav>
      </header>

      {/* Results Section */}
      <section className="py-12 blueprint-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* Project Header */}
          <div className="bg-card border border-border rounded-sm p-6 shadow-sm">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">{project.project_name}</h1>
                <p className="text-lg text-muted-foreground">{project.property_details.society_name}, {project.property_details.location}</p>
                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                  <p>{project.property_details.is_tod ? 'TOD' : 'Non-TOD'} Project</p>
                  {project.property_details.village_name && (
                    <>
                      <span>•</span>
                      <p>Village: {project.property_details.village_name}</p>
                    </>
                  )}
                  {project.property_details.rr_zone && (
                    <>
                      <span>•</span>
                      <p>Zone: {project.property_details.rr_zone}</p>
                    </>
                  )}
                </div>
              </div>
              <div className={`px-4 py-2 rounded-sm border ${
                isViable 
                  ? 'bg-chart-1/10 border-chart-1 text-chart-1' 
                  : 'bg-destructive/10 border-destructive text-destructive'
              }`}>
                <p className="text-sm font-medium uppercase tracking-wider" data-testid="viability-status">
                  {isViable ? 'Viable Project' : 'Needs Review'}
                </p>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="rounded-sm shadow-sm" data-testid="total-revenue-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono tracking-tight">
                  {formatCurrency(results.total_revenue)}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-sm shadow-sm" data-testid="total-costs-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Total Costs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono tracking-tight">
                  {formatCurrency(results.total_costs)}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-sm shadow-sm" data-testid="profit-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Net Profit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold font-mono tracking-tight ${
                  results.profit >= 0 ? 'text-chart-1' : 'text-destructive'
                }`}>
                  {formatCurrency(results.profit)}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-sm shadow-sm" data-testid="roi-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  ROI
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold font-mono tracking-tight ${
                  results.roi >= 0 ? 'text-chart-1' : 'text-destructive'
                }`}>
                  {formatPercentage(results.roi)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* FSI Breakdown Card */}
            {fsiBreakdown && (
              <Card className="lg:col-span-2 rounded-sm shadow-sm border-accent/50" data-testid="fsi-summary-card">
                <CardHeader>
                  <CardTitle className="font-heading tracking-tight text-accent">FSI Breakdown Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Permissible FSI</p>
                      <p className="text-xl font-bold font-mono text-accent">{fsiBreakdown.permissible_fsi.toFixed(2)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Total TDR</p>
                      <p className="text-xl font-bold font-mono">{fsiBreakdown.total_tdr.toFixed(2)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Buildable</p>
                      <p className="text-xl font-bold font-mono">{fsiBreakdown.total_buildable_area.toLocaleString()} sq ft</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">With Ancillary</p>
                      <p className="text-xl font-bold font-mono text-chart-1">{fsiBreakdown.total_fsi_with_ancillary.toLocaleString()} sq ft</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Charts */}
            {/* Revenue vs Cost */}
            <Card className="rounded-sm shadow-sm" data-testid="revenue-cost-chart">
              <CardHeader>
                <CardTitle className="font-heading tracking-tight flex items-center gap-2">
                  <BarChart className="h-5 w-5" />
                  Financial Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueVsCostData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={TOOLTIP_STYLE}
                    />
                    <Legend />
                    <Bar dataKey="Total Costs" fill="hsl(var(--chart-2))" />
                    <Bar dataKey="Total Revenue" fill="hsl(var(--chart-1))" />
                    <Bar dataKey="Profit" fill="hsl(var(--accent))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Breakdown */}
            <Card className="rounded-sm shadow-sm" data-testid="revenue-breakdown-chart">
              <CardHeader>
                <CardTitle className="font-heading tracking-tight flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Revenue Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={revenueBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {revenueBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={REVENUE_COLORS[index % REVENUE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Breakdown */}
            <Card className="rounded-sm shadow-sm lg:col-span-2" data-testid="cost-breakdown-chart">
              <CardHeader>
                <CardTitle className="font-heading tracking-tight flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Cost Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={costBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {costBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="rounded-sm shadow-sm" data-testid="detailed-breakdown">
              <CardHeader>
                <CardTitle className="font-heading tracking-tight">Property & Development Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Property Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plot Area</span>
                        <span className="font-mono">{project.property_details.plot_area_per_card.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Road Width</span>
                        <span className="font-mono">{project.property_details.road_width} m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Members</span>
                        <span className="font-mono">{project.property_details.num_members}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Parkings</span>
                        <span className="font-mono">{project.property_details.allotted_parkings}</span>
                      </div>
                    </div>
                    <Separator />
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Existing Areas</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Flats</span>
                        <span className="font-mono">{project.property_details.area_flats.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Offices</span>
                        <span className="font-mono">{project.property_details.area_offices.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Shops</span>
                        <span className="font-mono">{project.property_details.area_shops.toLocaleString()} sq ft</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Development</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Saleable Area</span>
                        <span className="font-mono">{project.development_parameters.proposed_built_up_area.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">FSI</span>
                        <span className="font-mono">{project.development_parameters.fsi}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Units</span>
                        <span className="font-mono">{project.development_parameters.num_units}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Floors</span>
                        <span className="font-mono">{project.development_parameters.num_floors}</span>
                      </div>
                    </div>
                    <Separator />
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Proposed Areas</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Flats</span>
                        <span className="font-mono">{project.development_parameters.proposed_flats_area.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Offices</span>
                        <span className="font-mono">{project.development_parameters.proposed_offices_area.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Shops</span>
                        <span className="font-mono">{project.development_parameters.proposed_shops_area.toLocaleString()} sq ft</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FSI Detailed Breakdown */}
            {fsiBreakdown && (
              <Card className="rounded-sm shadow-sm border-accent/30" data-testid="fsi-detailed-breakdown">
                <CardHeader>
                  <CardTitle className="font-heading tracking-tight text-accent">Detailed FSI Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">FSI Components</h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Basic FSI (1.1)</span>
                          <span className="font-mono">{fsiBreakdown.basic_fsi_area.toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Paid FSI</span>
                          <span className="font-mono">{fsiBreakdown.paid_fsi_area.toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total TDR Area</span>
                          <span className="font-mono">{fsiBreakdown.tdr_area.toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between pl-4 text-xs">
                          <span className="text-muted-foreground">• Amenity TDR ({((fsiBreakdown.amenity_tdr / fsiBreakdown.total_tdr) * 100).toFixed(0)}%)</span>
                          <span className="font-mono">{(fsiBreakdown.amenity_tdr * fsiBreakdown.gross_plot_area).toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between pl-4 text-xs">
                          <span className="text-muted-foreground">• Slum TDR ({((fsiBreakdown.slum_tdr / fsiBreakdown.total_tdr) * 100).toFixed(0)}%)</span>
                          <span className="font-mono">{(fsiBreakdown.slum_tdr * fsiBreakdown.gross_plot_area).toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Green FSI</span>
                          <span className="font-mono">{fsiBreakdown.green_fsi_area.toFixed(2)} sq ft</span>
                        </div>
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Ancillary FSI</h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Residential (60%)</span>
                          <span className="font-mono">{fsiBreakdown.ancillary_fsi_residential.toFixed(2)} sq ft</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Commercial (80%)</span>
                          <span className="font-mono">{fsiBreakdown.ancillary_fsi_commercial.toFixed(2)} sq ft</span>
                        </div>
                      </div>
                    </div>
                    <Separator />
                    <div className="bg-accent/10 p-3 rounded-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-accent">Total FSI Area</span>
                        <span className="font-mono font-bold text-lg text-accent">{fsiBreakdown.total_fsi_with_ancillary.toFixed(2)} sq ft</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Revenue Analysis */}
          <Card className="rounded-sm shadow-sm" data-testid="revenue-analysis">
            <CardHeader>
              <CardTitle className="font-heading tracking-tight">Financial Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Revenue Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">From Flats</span>
                      <span className="font-mono">{formatCurrency(results.revenue_from_flats)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">From Offices</span>
                      <span className="font-mono">{formatCurrency(results.revenue_from_offices)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">From Shops</span>
                      <span className="font-mono">{formatCurrency(results.revenue_from_shops)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Profitability Metrics</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Profit Margin</span>
                      <span className="font-mono font-semibold">{formatPercentage(results.profit_margin)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Construction/sq ft</span>
                      <span className="font-mono">{formatCurrency(project.financial_parameters.construction_cost_per_sqft)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ResultsView;